/**
 * Table for the detailed reports: search, sort by any column, pagination,
 * click a row for details, and export exactly what is shown (filtered + sorted) to Excel (CSV).
 */
import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import { Download as DownloadIcon, Search as SearchIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { downloadCsv } from '../../utils/csv';

export interface ReportColumn<Row> {
  key: string;
  label: string;
  align?: 'left' | 'right' | 'center';
  /** Value used for sorting and for the exported file */
  value: (row: Row) => string | number | null;
  /** Optional custom cell (defaults to `value`) */
  render?: (row: Row) => React.ReactNode;
}

interface DetailedReportTableProps<Row> {
  rows: Row[];
  columns: ReportColumn<Row>[];
  /** Columns written to the Excel file (defaults to the table columns) */
  exportColumns?: ReportColumn<Row>[];
  rowKey: (row: Row) => string | number;
  /** Text the search box matches against */
  searchText: (row: Row) => string;
  searchPlaceholder: string;
  exportFileName: string;
  canExport: boolean;
  /** Replaces the built-in CSV export (e.g. an Excel file built by the API) */
  onExport?: () => void;
  exporting?: boolean;
  onRowClick?: (row: Row) => void;
  loading?: boolean;
  defaultSort?: { key: string; direction: 'asc' | 'desc' };
}

function DetailedReportTable<Row>({
  rows,
  columns,
  exportColumns,
  rowKey,
  searchText,
  searchPlaceholder,
  exportFileName,
  canExport,
  onExport,
  exporting,
  onRowClick,
  loading,
  defaultSort,
}: DetailedReportTableProps<Row>) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState(defaultSort?.key ?? '');
  const [direction, setDirection] = useState<'asc' | 'desc'>(defaultSort?.direction ?? 'desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const visible = useMemo(() => {
    const term = search.trim().toLocaleLowerCase();
    const filtered = term ? rows.filter((row) => searchText(row).toLocaleLowerCase().includes(term)) : rows;
    const column = columns.find((c) => c.key === sortKey);
    if (!column) return filtered;
    return [...filtered].sort((a, b) => {
      const x = column.value(a);
      const y = column.value(b);
      if (x === y) return 0;
      if (x === null || x === '') return 1; // empty values always last
      if (y === null || y === '') return -1;
      const order = typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y));
      return direction === 'asc' ? order : -order;
    });
  }, [rows, columns, search, searchText, sortKey, direction]);

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setDirection(direction === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setDirection('desc');
    }
  };

  const handleExport = () => downloadCsv(exportColumns ?? columns, visible, exportFileName);

  const pageRows = visible.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder={searchPlaceholder}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          sx={{ flex: 1, minWidth: 240 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Typography variant="body2" color="text.secondary">
          {t('reports.detailed.rowsCount', { count: visible.length })}
        </Typography>
        {canExport && (
          <Button
            variant="outlined"
            startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
            onClick={onExport ?? handleExport}
            disabled={!visible.length || exporting}
          >
            {t('reports.detailed.exportExcel')}
          </Button>
        )}
      </Box>

      <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  align={column.align}
                  sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
                  sortDirection={sortKey === column.key ? direction : false}
                >
                  <TableSortLabel
                    active={sortKey === column.key}
                    direction={sortKey === column.key ? direction : 'desc'}
                    onClick={() => handleSort(column.key)}
                  >
                    {column.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                  {t('common.loading')}
                </TableCell>
              </TableRow>
            ) : pageRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  {t('reports.detailed.noData')}
                </TableCell>
              </TableRow>
            ) : (
              pageRows.map((row) => (
                <TableRow
                  key={rowKey(row)}
                  hover
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  sx={onRowClick ? { cursor: 'pointer' } : undefined}
                >
                  {columns.map((column) => (
                    <TableCell key={column.key} align={column.align} sx={{ whiteSpace: 'nowrap' }}>
                      {column.render ? column.render(row) : (column.value(row) ?? '—')}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={visible.length}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
        labelRowsPerPage={t('reports.detailed.rowsPerPage')}
        labelDisplayedRows={({ from, to, count }) => t('reports.detailed.displayedRows', { from, to, count })}
        onPageChange={(_e, p) => setPage(p)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    </Box>
  );
}

export default DetailedReportTable;
